/**
 * "Cần xử lý" under the domain page header, on every tab (owner 2026-09-29: errors were hidden in
 * sub-tabs, a stopped registration showed nothing on the overview). Each item says what is wrong
 * and has its fix one click away: an action dialog (`?action=`) or the tab that resolves it.
 */

import type { TFunction } from 'i18next';
import { AlertTriangle, ArrowRight, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DomainDetail, RegistryTransaction } from '../../api';
import { Badge, type Tone } from '../../components/Badge';
import { daysUntil, formatRelative } from '../../lib/format';
import { LinkedWork } from '../work/LinkedWork';
import { useVocab } from './refs';
import { registrationItem } from './registration';
import { decidableStop, reactivableStop, RegistrationStopPanel } from './RegistrationStop';
import { COMPLIANCE, DOSSIER, LIFECYCLE, SERVICE, useStatusLabel } from './status';
import { UNRESOLVED_STATES } from './ribbon';
import { domainTabUrl, type DomainTabId } from './tabs/moved';

export interface Attention {
  tone: Tone;
  text: string;
  to: string;
  cta: string;
  /** The tab whose own content is this item (not repeated as a banner on that tab). */
  tab?: DomainTabId;
}

/** Error class of a command an upstream registrar accepted as a pending order (followed until settled). */
const PENDING_ORDER = 'WEBNIC_PENDING_ORDER';

/** What an operator should look at first, derived from the domain record, most urgent first. */
export function attentionItems(
  detail: DomainDetail,
  unresolved: number,
  t: TFunction<'domains'>,
  tu: TFunction<'ux'>,
  label: ReturnType<typeof useStatusLabel>,
  vocab: ReturnType<typeof useVocab>,
  contactDrift = 0,
): Attention[] {
  const d = detail.domain;
  const url = (tab: DomainTabId, section?: string, action?: string) => domainTabUrl(d.ascii_name, tab, section, action);
  const items: Attention[] = [];
  // The Registry tab shows the refused command with its technical detail. A failed registration
  // that can be reactivated says it in its panel (with the refused command): not twice.
  const failed = reactivableStop(detail.registration) ? null : registrationItem(detail.registration, t);
  if (failed) items.push({ ...failed, to: url('registry'), cta: tu('domain.cta.registry') });
  if (unresolved > 0) items.push({ tone: 'serious', text: t('overview.unresolved', { count: unresolved }), to: url('registry'), cta: tu('domain.cta.registry') });
  if (d.service_state !== 'NORMAL') items.push({ tone: 'crit', text: t('overview.service', { state: label(SERVICE, d.service_state) }), to: url('compliance'), cta: tu('domain.cta.compliance') });
  if (d.compliance_state !== 'COMPLIANT' && d.compliance_state !== 'UNKNOWN') {
    items.push({
      tone: d.compliance_state === 'ACTION_REQUIRED' ? 'warn' : 'crit',
      text: t('overview.compliance', { state: label(COMPLIANCE, d.compliance_state) }),
      to: url('compliance'),
      cta: tu('domain.cta.compliance'),
    });
  }
  const legal = detail.locks.filter((l) => l.lock_type !== 'REGISTRAR_LOCK' && l.lock_type !== 'TRANSFER_LOCK');
  if (legal.length) {
    items.push({ tone: 'warn', text: t('overview.locks', { count: legal.length, list: legal.map((l) => vocab('lock', l.lock_type)).join(', ') }), to: url('transfer'), cta: tu('domain.cta.locks') });
  }
  if (contactDrift > 0) items.push({ tone: 'warn', text: tu('domain.contactDrift', { count: contactDrift }), to: url('subject', 'contacts'), cta: tu('domain.cta.contacts') });
  const dossier = detail.dossier?.status ?? d.dossier_state;
  // A registration waiting for its dossier already says so (the stop panel): one warning, not two.
  const waitingForDossier = detail.registration?.state === 'WAITING' && Boolean(detail.registration.waiting_for);
  if (dossier !== 'COMPLETE' && !waitingForDossier) items.push({ tone: 'warn', text: t('overview.dossier', { state: label(DOSSIER, dossier) }), to: url('dossier'), cta: tu('domain.cta.dossier'), tab: 'dossier' });
  if (d.transfer_state !== 'NONE') items.push({ tone: 'warn', text: t('overview.transferPending'), to: url('transfer', 'transfers'), cta: tu('domain.cta.transfers') });
  if (d.billing_state === 'OVERDUE') items.push({ tone: 'serious', text: t('overview.overdue'), to: url('finance'), cta: tu('domain.cta.billing') });
  const renewable = d.lifecycle_state === 'ACTIVE' || d.lifecycle_state === 'GRACE_PERIOD';
  if (d.registry_expires_at && d.customer_expires_at && new Date(d.customer_expires_at) < new Date(d.registry_expires_at)) {
    items.push({
      tone: 'warn',
      text: t('overview.paidThrough', { customer: formatRelative(d.customer_expires_at), registry: formatRelative(d.registry_expires_at) }),
      to: url('lifecycle'),
      cta: tu('domain.cta.lifecycle'),
    });
  }
  if (d.registry_expires_at) {
    const days = daysUntil(d.registry_expires_at);
    if (!d.auto_renew && days >= 0 && days <= 45) {
      items.push({
        tone: 'warn',
        text: t('overview.expiresNoRenew', { when: formatRelative(d.registry_expires_at) }),
        to: renewable ? url('overview', undefined, 'renew') : url('lifecycle'),
        cta: renewable ? tu('domain.cta.renew') : tu('domain.cta.lifecycle'),
      });
    }
  }
  if (['GRACE_PERIOD', 'REDEMPTION', 'PENDING_DELETE', 'EXPIRED', 'CREATE_FAILED'].includes(d.lifecycle_state)) {
    items.push({
      tone: 'serious',
      text: t('overview.lifecycle', { state: label(LIFECYCLE, d.lifecycle_state) }),
      to: d.lifecycle_state === 'GRACE_PERIOD' ? url('overview', undefined, 'renew') : url('lifecycle'),
      cta: d.lifecycle_state === 'GRACE_PERIOD' ? tu('domain.cta.renew') : tu('domain.cta.lifecycle'),
    });
  }
  const rank: Record<Tone, number> = { crit: 0, serious: 1, warn: 2, pending: 3, info: 4, neutral: 5, ok: 6 };
  return items.sort((a, b) => rank[a.tone] - rank[b.tone]);
}

/** The domain's attention items; `txs` are its registry commands (null while loading or refused). */
export function useDomainAttention(detail: DomainDetail, txs: readonly RegistryTransaction[] | null, contactDrift = 0): Attention[] {
  const { t } = useTranslation('domains');
  const { t: tu } = useTranslation('ux');
  const label = useStatusLabel();
  const vocab = useVocab();
  const open = (txs ?? []).filter((x) => UNRESOLVED_STATES.has(x.state));
  // An upstream registrar's pending order is in progress, not an unknown outcome.
  const inProgress = open.filter((x) => x.state === 'UNKNOWN' && x.error_class === PENDING_ORDER).length;
  const items = attentionItems(detail, open.length - inProgress, t, tu, label, vocab, contactDrift);
  if (inProgress > 0) {
    items.push({ tone: 'pending', text: t('overview.pendingOrder', { count: inProgress }), to: domainTabUrl(detail.domain.ascii_name, 'registry'), cta: tu('domain.cta.registry') });
  }
  return items;
}

const BANNER: Record<Tone, string> = {
  crit: 'border-critical/30 bg-critical-soft text-critical',
  serious: 'border-serious/30 bg-serious-soft text-serious',
  warn: 'border-warning/30 bg-warning-soft text-warning',
  pending: 'border-info/20 bg-info-soft text-info',
  info: 'border-info/20 bg-info-soft text-info',
  neutral: 'border-border bg-card text-muted-foreground',
  ok: 'border-success/30 bg-success-soft text-success',
};

/**
 * "Cần xử lý" under the header, on every tab (v2.1 banner): a stopped registration (with Thử lại
 * / Huỷ đăng ký) and open work items first, then the most urgent item as a banner with its fix one
 * click away, the others behind "N việc khác".
 */
export function DomainAttention({
  detail,
  txs,
  txFailed,
  contactDrift,
  onChanged,
  tab,
}: {
  detail: DomainDetail;
  /** The open tab: an item about it is not repeated above it. */
  tab?: DomainTabId;
  txs: readonly RegistryTransaction[] | null;
  txFailed: boolean;
  /** Current contacts that now differ from their registrant. */
  contactDrift: number;
  onChanged: () => void;
}) {
  const { t } = useTranslation('ux');
  const { t: td } = useTranslation('domains');
  const attention = useDomainAttention(detail, txs, contactDrift);
  const items = attention.filter((a) => !tab || a.tab !== tab);
  const [all, setAll] = useState(false);
  const stopped = decidableStop(detail.registration) || reactivableStop(detail.registration);
  const [first, ...rest] = items;
  return (
    <section aria-label={t('actionBar.attention')} className="mb-4 space-y-3" data-domain-attention>
      {stopped ? <RegistrationStopPanel detail={detail} onChanged={onChanged} /> : null}
      <LinkedWork domainId={detail.domain.id} empty={!attention.length && !stopped && !txFailed && txs !== null ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="size-4 text-success" aria-hidden="true" /> {t('actionBar.allClear')}
        </p>
      ) : null} />
      {first ? (
        <div className={cn('rounded-xl border', BANNER[first.tone])}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
            <AlertTriangle className="size-4.5 shrink-0" aria-hidden="true" />
            <div className="min-w-[12rem] flex-1">
              <div className="text-[13px] font-semibold">{first.tone === 'serious' || first.tone === 'crit' ? td('overview.action') : td('overview.check')}</div>
              <div className="text-[12.5px] leading-relaxed">{first.text}</div>
            </div>
            <Link to={first.to} className="inline-flex items-center gap-1 text-[12.5px] font-semibold hover:underline">
              {first.cta} <ChevronRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
          {rest.length ? (
            <>
              {all ? (
                <ul className="divide-y border-t border-current/15 bg-card text-foreground">
                  {rest.map((a) => (
                    <li key={a.text} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2">
                      <Badge tone={a.tone}>{a.tone === 'serious' || a.tone === 'crit' ? td('overview.action') : td('overview.check')}</Badge>
                      <span className="min-w-[12rem] flex-1 text-sm">{a.text}</span>
                      <Button asChild variant="outline" size="sm">
                        <Link to={a.to}>
                          {a.cta} <ArrowRight />
                        </Link>
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <button
                type="button"
                className="flex w-full items-center justify-center gap-1 border-t border-current/15 px-4 py-1.5 text-xs font-medium"
                onClick={() => setAll((v) => !v)}
                aria-expanded={all}
              >
                <ChevronDown className={cn('size-3.5 transition-transform', all && 'rotate-180')} aria-hidden="true" />
                {all ? t('actionBar.showLess') : t('actionBar.showAll', { count: rest.length })}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {txFailed ? <p className="text-xs text-muted-foreground">{td('overview.txFailed')}</p> : null}
    </section>
  );
}
